import type { ReactNode } from 'react';
import type React from 'react';
import { createContext, useCallback, useContext, useState } from 'react';

interface FeatureFlags {
  enableScalablePaymentsView: boolean;
}

interface FeatureFlagsContextType {
  featureFlags: FeatureFlags;
  updateFeatureFlags: (flags: Partial<FeatureFlags>) => void;
  setEnableScalablePaymentsView: (enabled: boolean) => void;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextType | undefined>(undefined);

interface FeatureFlagsProviderProps {
  children: ReactNode;
}

export const FeatureFlagsProvider: React.FC<FeatureFlagsProviderProps> = ({ children }) => {
  const [featureFlags, setFeatureFlags] = useState<FeatureFlags>({
    enableScalablePaymentsView: false, // Default to false
  });

  const updateFeatureFlags = useCallback((newFlags: Partial<FeatureFlags>) => {
    setFeatureFlags((prev) => ({
      ...prev,
      ...newFlags,
    }));
  }, []);

  const setEnableScalablePaymentsView = useCallback((enabled: boolean) => {
    setFeatureFlags((prev) => ({
      ...prev,
      enableScalablePaymentsView: enabled,
    }));
  }, []);

  const value: FeatureFlagsContextType = {
    featureFlags,
    updateFeatureFlags,
    setEnableScalablePaymentsView,
  };

  return <FeatureFlagsContext.Provider value={value}>{children}</FeatureFlagsContext.Provider>;
};

export const useFeatureFlags = (): FeatureFlagsContextType => {
  const context = useContext(FeatureFlagsContext);
  if (context === undefined) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagsProvider');
  }
  return context;
};

// Convenience hook for just the scalable payments view flag
export const useScalablePaymentsView = (): boolean => {
  const { featureFlags } = useFeatureFlags();
  return featureFlags.enableScalablePaymentsView;
};
